// Thai Tax Calculator - ปีภาษี 2568
// Ported from LEGACY taxCalculator.ts → pure JavaScript, no state
//
// ⚠️ RETIRED (S1-P3): this hardcoded calculator is SUPERSEDED by the config-driven,
// versioned formula engine exposed via `POST api/v1/TaxCalculate/.../run`
// (see services/taxCalculateApi.js + views/TaxCalculate*.vue). It is kept ONLY as an
// offline fallback for the pages that still import it directly (TaxSandbox.vue,
// TaxHistoryDetail.vue, taxSandboxStore.js, taxHistoryStore.js). Do NOT add new
// callers — use the stateless /run API instead.

/**
 * @returns {object} Zero-valued default tax input
 */
export function getDefaultTaxInput() {
  return {
    formType: '90',
    income40_1: 0,
    income40_2: 0,
    income40_3: 0,
    income40_4: 0,
    income40_5: 0,
    income40_6: 0,
    income40_7: 0,
    income40_8: 0,
    expense40_5: null,
    expense40_5_rate: 30,
    expense40_6_rate: 30,
    expense40_6: null,
    expense40_7: null,
    expense40_8: null,
    hasSpouse: false,
    spouseNoIncome: false,
    pregnancyCost: 0,
    childrenAfter2561: 0,
    childrenBefore2561: 0,
    deductFather: false,
    deductMother: false,
    deductSpouseFather: false,
    deductSpouseMother: false,
    disabledSpouse: false,
    disabledFather: false,
    disabledMother: false,
    disabledChild: false,
    disabledOther: false,
    socialSecurity: 0,
    lifeInsurance: 0,
    healthInsurance: 0,
    parentHealthInsurance: 0,
    pensionInsurance: 0,
    rmf: 0,
    pvd: 0,
    nsf: 0,
    housingInterest: 0,
    politicalDonation: 0,
    educationDonation: 0,
    generalDonation: 0,
    easyEReceipt: 0,
    socialEnterprise: 0,
    thaiESG: 0,
    homeBuild: 0,
    thaiESGX: 0,
    ltfToESGX: 0,
    travelSecondary: 0,
    travelOther: 0,
    withholdingTax: 0,
  };
}

/**
 * @param {object} input - Tax input
 * @returns {number} Sum of all income fields
 */
export function getTotalIncome(input) {
  return (
    input.income40_1 + input.income40_2 + input.income40_3 + input.income40_4 +
    input.income40_5 + input.income40_6 + input.income40_7 + input.income40_8
  );
}

/**
 * @param {object} input - Tax input
 * @returns {number} Expense deduction amount
 */
export function getExpenseDeduction(input) {
  // 40(1)+(2) combined cap 100k — always auto
  const exp12 = Math.min((input.income40_1 + input.income40_2) * 0.5, 100000);
  const exp3 = Math.min(input.income40_3 * 0.5, 100000);
  // 40(5-8): use actual expense if provided, otherwise flat rate
  const exp5 = input.expense40_5 != null ? input.expense40_5 : input.income40_5 * ((input.expense40_5_rate ?? 30) / 100);
  const exp6 = input.expense40_6 != null ? input.expense40_6 : input.income40_6 * ((input.expense40_6_rate ?? 30) / 100);
  const exp7 = input.expense40_7 != null ? input.expense40_7 : input.income40_7 * 0.6;
  const exp8 = input.expense40_8 != null ? input.expense40_8 : input.income40_8 * 0.6;
  return exp12 + exp3 + exp5 + exp6 + exp7 + exp8;
}

/**
 * @param {number} netIncome
 * @returns {{ tax: number, rate: number }} Progressive tax and effective rate
 */
export function calculateProgressiveTax(netIncome) {
  const brackets = [
    { limit: 150000, rate: 0 },
    { limit: 300000, rate: 0.05 },
    { limit: 500000, rate: 0.10 },
    { limit: 750000, rate: 0.15 },
    { limit: 1000000, rate: 0.20 },
    { limit: 2000000, rate: 0.25 },
    { limit: 5000000, rate: 0.30 },
    { limit: Infinity, rate: 0.35 },
  ];

  let tax = 0;
  let remaining = netIncome;
  let prevLimit = 0;
  let highestRate = 0;

  for (const bracket of brackets) {
    const taxableInBracket = Math.min(remaining, bracket.limit - prevLimit);
    if (taxableInBracket <= 0) break;
    tax += taxableInBracket * bracket.rate;
    remaining -= taxableInBracket;
    if (bracket.rate > 0) highestRate = bracket.rate;
    prevLimit = bracket.limit;
  }

  return { tax, rate: highestRate * 100 };
}

/**
 * @param {object} input - Full tax input
 * @returns {object} Complete tax calculation result
 */
export function calculateTax(input) {
  const totalIncome = getTotalIncome(input);
  const expenseDeduction = getExpenseDeduction(input);

  const isHalfYear = input.formType === '94';
  const deductionDetails = [];

  // ค่าลดหย่อนส่วนตัว: 60k เต็มปี / 30k ครึ่งปี
  const personalDeduction = isHalfYear ? 30000 : 60000;
  deductionDetails.push({ label: 'ค่าลดหย่อนส่วนตัว', amount: personalDeduction });

  // ค่าลดหย่อนคู่สมรส
  let spouseDeduction = 0;
  if (input.hasSpouse && input.spouseNoIncome) {
    spouseDeduction = isHalfYear ? 30000 : 60000;
    deductionDetails.push({ label: 'ค่าลดหย่อนคู่สมรส', amount: spouseDeduction });
  }

  // ค่าฝากครรภ์/ทำคลอด
  const pregnancyCost = Math.min(input.pregnancyCost, isHalfYear ? 30000 : 60000);
  if (pregnancyCost > 0) {
    deductionDetails.push({ label: 'ค่าฝากครรภ์/ทำคลอด', amount: pregnancyCost });
  }

  // ค่าลดหย่อนบุตร
  const childDeduction = isHalfYear
    ? input.childrenAfter2561 * 30000 + input.childrenBefore2561 * 15000
    : input.childrenAfter2561 * 60000 + input.childrenBefore2561 * 30000;
  if (childDeduction > 0) {
    deductionDetails.push({ label: 'ค่าลดหย่อนบุตร', amount: childDeduction });
  }

  // ค่าลดหย่อนบิดามารดา
  const perParent = isHalfYear ? 15000 : 30000;
  let parentDeduction = 0;
  if (input.deductFather) parentDeduction += perParent;
  if (input.deductMother) parentDeduction += perParent;
  if (input.deductSpouseFather) parentDeduction += perParent;
  if (input.deductSpouseMother) parentDeduction += perParent;
  if (parentDeduction > 0) {
    deductionDetails.push({ label: 'ค่าลดหย่อนบิดามารดา', amount: parentDeduction });
  }

  // ค่าลดหย่อนผู้พิการ
  const perDisabled = isHalfYear ? 30000 : 60000;
  let disabledDeduction = 0;
  if (input.disabledSpouse) disabledDeduction += perDisabled;
  if (input.disabledFather) disabledDeduction += perDisabled;
  if (input.disabledMother) disabledDeduction += perDisabled;
  if (input.disabledChild) disabledDeduction += perDisabled;
  if (input.disabledOther) disabledDeduction += perDisabled;
  if (disabledDeduction > 0) {
    deductionDetails.push({ label: 'ค่าลดหย่อนผู้พิการ', amount: disabledDeduction });
  }

  // เงินประกันสังคม (ไม่เกิน 10,500)
  const socialSecurity = Math.min(input.socialSecurity, 10500);
  if (socialSecurity > 0) {
    deductionDetails.push({ label: 'เงินประกันสังคม', amount: socialSecurity });
  }

  // เบี้ยประกันชีวิต: ≤ 100k เต็มปี / ≤ 10k ครึ่งปี
  const lifeInsuranceCap = isHalfYear ? 10000 : 100000;
  const lifeInsurance = Math.min(input.lifeInsurance, lifeInsuranceCap);
  if (lifeInsurance > 0) {
    deductionDetails.push({ label: 'เบี้ยประกันชีวิตทั่วไป', amount: lifeInsurance });
  }

  // เบี้ยประกันสุขภาพตนเอง: ≤ 25k (เต็มปี: รวมชีวิตไม่เกิน 100k)
  const healthInsurance = isHalfYear
    ? Math.min(input.healthInsurance, 25000)
    : Math.min(input.healthInsurance, 25000, 100000 - lifeInsurance);
  if (healthInsurance > 0) {
    deductionDetails.push({ label: 'เบี้ยประกันสุขภาพตนเอง', amount: healthInsurance });
  }

  // เบี้ยประกันสุขภาพบิดามารดา: ≤ 15k เต็มปี / ไม่มีสิทธิ์ครึ่งปี
  const parentHealthInsurance = isHalfYear ? 0 : Math.min(input.parentHealthInsurance, 15000);
  if (parentHealthInsurance > 0) {
    deductionDetails.push({ label: 'เบี้ยประกันสุขภาพบิดามารดา', amount: parentHealthInsurance });
  }

  // เบี้ยประกันชีวิตแบบบำนาญ (≤ 15% ของรายได้ ≤ 200k)
  const pensionInsurance = Math.min(input.pensionInsurance, totalIncome * 0.15, 200000);
  if (pensionInsurance > 0) {
    deductionDetails.push({ label: 'เบี้ยประกันชีวิตแบบบำนาญ', amount: pensionInsurance });
  }

  // RMF (≤ 30% ของรายได้ ≤ 500k)
  const rmf = Math.min(input.rmf, totalIncome * 0.3, 500000);
  if (rmf > 0) {
    deductionDetails.push({ label: 'กองทุน RMF', amount: rmf });
  }

  // กบข./กองทุนสำรองเลี้ยงชีพ (≤ 15% ≤ 500k)
  const pvd = Math.min(input.pvd, totalIncome * 0.15, 500000);
  if (pvd > 0) {
    deductionDetails.push({ label: 'กบข./กองทุนสำรองเลี้ยงชีพ', amount: pvd });
  }

  // กองทุนการออมแห่งชาติ (≤ 30k)
  const nsf = Math.min(input.nsf, 30000);
  if (nsf > 0) {
    deductionDetails.push({ label: 'กองทุนการออมแห่งชาติ (กอช.)', amount: nsf });
  }

  // เพดานกลุ่มเกษียณรวม ≤ 500k
  const retirementGroupCap = 500000;
  const retirementTotal = pensionInsurance + rmf + pvd + nsf;
  const retirementExcess = Math.max(0, retirementTotal - retirementGroupCap);

  // ดอกเบี้ยซื้อที่อยู่อาศัย (≤ 100k)
  const housingInterest = Math.min(input.housingInterest, 100000);
  if (housingInterest > 0) {
    deductionDetails.push({ label: 'ดอกเบี้ยซื้อที่อยู่อาศัย', amount: housingInterest });
  }

  // เงินบริจาคพรรคการเมือง (≤ 10k)
  const politicalDonation = Math.min(input.politicalDonation, 10000);
  if (politicalDonation > 0) {
    deductionDetails.push({ label: 'เงินบริจาคพรรคการเมือง', amount: politicalDonation });
  }

  // Easy E-Receipt 2.0 (≤ 50k; ไม่ใช้ใน ภงด 94)
  const easyEReceipt = isHalfYear ? 0 : Math.min(input.easyEReceipt, 50000);
  if (easyEReceipt > 0) {
    deductionDetails.push({ label: 'Easy E-Receipt 2.0', amount: easyEReceipt });
  }

  // Social Enterprise (≤ 100k)
  const socialEnterprise = Math.min(input.socialEnterprise, 100000);
  if (socialEnterprise > 0) {
    deductionDetails.push({ label: 'เงินลงทุน Social Enterprise', amount: socialEnterprise });
  }

  // Thai ESG (≤ 30% ≤ 300k)
  const thaiESG = Math.min(input.thaiESG, totalIncome * 0.3, 300000);
  if (thaiESG > 0) {
    deductionDetails.push({ label: 'กองทุน Thai ESG', amount: thaiESG });
  }

  // ค่าจ้างก่อสร้างบ้าน (≤ 100k)
  const homeBuild = Math.min(input.homeBuild, 100000);
  if (homeBuild > 0) {
    deductionDetails.push({ label: 'ค่าจ้างก่อสร้างบ้าน', amount: homeBuild });
  }

  // Thai ESGX (≤ 30% ≤ 300k)
  const thaiESGX = Math.min(input.thaiESGX, totalIncome * 0.3, 300000);
  if (thaiESGX > 0) {
    deductionDetails.push({ label: 'กองทุน Thai ESGX', amount: thaiESGX });
  }

  // สับเปลี่ยน LTF → Thai ESGX
  const ltfToESGX = input.ltfToESGX;
  if (ltfToESGX > 0) {
    deductionDetails.push({ label: 'สับเปลี่ยน LTF ไป Thai ESGX', amount: ltfToESGX });
  }

  // เที่ยวดีมีคืน (≤ 15k each)
  const travelSecondary = Math.min(input.travelSecondary, 15000);
  if (travelSecondary > 0) {
    deductionDetails.push({ label: 'เที่ยวดีมีคืน (เมืองรอง)', amount: travelSecondary });
  }
  const travelOther = Math.min(input.travelOther, 15000);
  if (travelOther > 0) {
    deductionDetails.push({ label: 'เที่ยวดีมีคืน (นอกจากเมืองรอง)', amount: travelOther });
  }

  const totalDeductionsBeforeDonation =
    personalDeduction +
    spouseDeduction +
    pregnancyCost +
    childDeduction +
    parentDeduction +
    disabledDeduction +
    socialSecurity +
    lifeInsurance +
    healthInsurance +
    parentHealthInsurance +
    pensionInsurance +
    rmf +
    pvd +
    nsf -
    retirementExcess +
    housingInterest +
    politicalDonation +
    easyEReceipt +
    socialEnterprise +
    thaiESG +
    homeBuild +
    thaiESGX +
    ltfToESGX +
    travelSecondary +
    travelOther;

  // เงินบริจาคเพื่อการศึกษาฯ (2 เท่า ≤ 10% ของเงินได้หลังหักค่าใช้จ่ายและลดหย่อน)
  const incomeAfterExpenseAndDeductions = totalIncome - expenseDeduction - totalDeductionsBeforeDonation;
  const educationDonation = Math.min(
    input.educationDonation * 2,
    incomeAfterExpenseAndDeductions * 0.1
  );
  if (educationDonation > 0) {
    deductionDetails.push({ label: 'เงินบริจาคเพื่อการศึกษาฯ (2 เท่า)', amount: educationDonation });
  }

  // เงินบริจาคทั่วไป (≤ 10%)
  const generalDonation = Math.min(
    input.generalDonation,
    (totalIncome - expenseDeduction - totalDeductionsBeforeDonation - educationDonation) * 0.1
  );
  if (generalDonation > 0) {
    deductionDetails.push({ label: 'เงินบริจาคทั่วไป', amount: generalDonation });
  }

  const totalDeductions = totalDeductionsBeforeDonation + educationDonation + generalDonation;
  const netIncome = Math.max(0, totalIncome - expenseDeduction - totalDeductions);
  const { tax: progressiveTax, rate: taxRate } = calculateProgressiveTax(netIncome);

  // ภาษีตามมาตรา 48(2) — 0.5% ของรายได้รวมยกเว้น 40(1) เมื่อรายได้รวม ≥ 120,000
  const incomeExcl40_1 = totalIncome - input.income40_1;
  const altMinTax = totalIncome >= 120000 ? incomeExcl40_1 * 0.005 : 0;

  // ใช้ AMT เมื่อ: AMT > 5,000 และ AMT > ภาษีขั้นบันได
  const usedAltMin = altMinTax > 5000 && altMinTax > progressiveTax;
  const taxBeforeCredit = usedAltMin ? altMinTax : progressiveTax;

  const withholdingTax = input.withholdingTax;
  const taxDiff = taxBeforeCredit - withholdingTax;
  const taxToPay = Math.max(0, taxDiff);
  const taxRefund = Math.max(0, -taxDiff);

  return {
    formType: input.formType,
    totalIncome,
    expenseDeduction,
    personalDeduction,
    totalDeductions,
    deductionDetails,
    netIncome,
    progressiveTax,
    altMinTax,
    usedAltMin,
    taxBeforeCredit,
    withholdingTax,
    taxToPay,
    taxRefund,
    taxRate,
  };
}

/**
 * @param {number} n
 * @returns {string} Thai-formatted number string
 */
export function formatNumber(n) {
  return n.toLocaleString('th-TH');
}
